import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Link } from "react-router-dom";

import FormInput from "@ats/src/components/forms/FormInput";
import DropdownMenu from "@ats/src/components/shared/DropdownMenu";
import AdminOrganizationEditModal from "@ats/src/views/admin/AdminOrganizationEditModal";
import AdminRemoveBadActorModal from "@ats/src/views/admin/AdminRemoveBadActorModal";
import AdminCareersPageSubscriptionsModal from "@ats/src/views/admin/AdminCareersPageSubscriptionsModal";
import AdminJobEditModal from "@ats/src/views/admin/AdminJobEditModal";

import { useSearchOrganizations } from "@shared/queryHooks/admin/useOrganization";
import { useInfiniteFreeJobs } from "@shared/queryHooks/admin/useAdminJobs";
import { useBecomeUser } from "@shared/queryHooks/admin/useBecome";

import Button from "@ats/src/components/shared/Button";
import { useModalContext } from "@shared/context/ModalContext";
import { capitalizeSingleWord } from "@ats/src/lib/utils/helpers";

function AdminDashboardFreeJobs(props: any) {
  const { openModal, removeModal } = useModalContext();

  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchResults, setSearchResults] = React.useState([]);

  // Infinite query for jobs sorted by published date
  const {
    data: infiniteJobsData,
    isFetching: isFetchingInfiniteJobs,
    isSuccess: isSuccessInfiniteJobs,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteFreeJobs();

  // Flatten infinite query pages into a single array of jobs
  const freeJobs = React.useMemo(() => {
    if (!infiniteJobsData?.pages) return [];
    return infiniteJobsData.pages.flatMap((page) => page.items || []);
  }, [infiniteJobsData]);

  // Create a Map for efficient job lookup by organization ID
  const jobsByOrganizationId = React.useMemo(() => {
    const map = new Map();
    freeJobs.forEach((job: any) => {
      if (job?.organization?.id) {
        // Use the first job found for each organization
        // (jobs are sorted by published_at desc, so this will be the most recent)
        if (!map.has(job.organization.id)) {
          map.set(job.organization.id, job);
        }
      }
    });
    return map;
  }, [freeJobs]);

  const isFetchingFree = isFetchingInfiniteJobs;
  const isSuccessFree = isSuccessInfiniteJobs;
  const { mutate: becomeUser, isLoading: isLoadingBecome } = useBecomeUser();

  const {
    mutate: searchOrganizations,
    isSuccess: isSuccessSearchOrganizations,
  } = useSearchOrganizations();

  window.logger("%c[AdminDashboardFreeJobs] render", "color: #1976D2", {
    props,
    freeJobs,
    infiniteJobsData,
    isFetchingFree,
    hasNextPage,
    pages: infiniteJobsData?.pages,
    lastPage: infiniteJobsData?.pages?.[infiniteJobsData.pages.length - 1],
  });

  const handleBecomeUser = (userId) => {
    window.logger("%c[AdminDashboardFreeJobs] handleBecomeUser userId", "color: #1976D2", userId);

    becomeUser(
      { userId },
      {
        onSuccess: (data) => {
          window.logger(
            "%c[AdminDashboardFreeJobs] becomeUser onSuccess",
            "background-color: #c6d219",
            { data },
          );
          window.location.href = `${window.APP_ATS_ROOT_URL}/jobs`;
        },
        onError: (response: any) => {
          window.logger("[AdminDashboardFreeJobs] becomeUser server error", { response });
          if (response.data.errors) {
            // setState({ ...state, errors: response.data.errors });
          }
        },
      },
    );
  };

  const onChangeSearchOrganizations = (_, value) => {
    window.logger("[AdminDashboardFreeJobs] onChangeSearchOrganizations", { value, type: typeof value, length: value?.length });
    setSearchTerm(value);
    if (value && value.length > 0) {
      window.logger("[AdminDashboardFreeJobs] Calling searchOrganizations with", value);
      searchOrganizations(value, {
        onSuccess: (data) => {
          setSearchResults(data.items || []);
        },
        onError: () => {
          setSearchResults([]);
        },
      });
    } else {
      window.logger("[AdminDashboardFreeJobs] NOT calling searchOrganizations - value is falsy or empty", { value });
      setSearchResults([]);
    }
  };

  const handleOnClickEditOrganization = (organization) => {
    const modal = <AdminOrganizationEditModal organization={organization} onCancel={removeModal} />;

    openModal(modal);
  };

  const handleOnClickEditJob = (job) => {
    const modal = <AdminJobEditModal job={job} onCancel={removeModal} />;
    openModal(modal);
  };

  const handleOnClickTakeover = (organization) => {
    const modal = (
      <AdminRemoveBadActorModal
        organizationId={organization.id}
        organizationName={organization.name}
        onCancel={removeModal}
      />
    );
    openModal(modal);
  };

  const handleOnClickUpdateSubscriptions = (organization) => {
    const modal = (
      <AdminCareersPageSubscriptionsModal organization={organization} onCancel={removeModal} />
    );
    openModal(modal);
  };

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const organizationNodes = () => {
    if (!isSuccessFree) {
      return [];
    }

    let organizations;
    if (searchResults.length > 0 && searchTerm.length > 0) {
      organizations = searchResults;
    } else if (freeJobs.length > 0) {
      // Extract organizations from jobs
      organizations = freeJobs.map((job: any) => job.organization);
    } else {
      return [];
    }

    window.logger("%c[AdminDashboardFreeJobs] organizationNodes", "color: #1976D2", {
      organizations,
    });

    return organizations.map(function (organization) {
      let disableBecome = organization?.owner?.email === "system@inflowhq.com";

      // Find the job for this organization by matching organization ID
      const job = organization?.id ? jobsByOrganizationId.get(organization.id) : null;

      return (
        <tr className="admin-organization-list__item" key={`org-${organization.id}`}>
          <td>{organization.id}</td>
          <td>{organization.name}</td>
          <td>
            <Link to={`/admin/org-jobs?organizationId=${organization.id}`}>
              {organization.publishedJobsCount} Job(s)
            </Link>
          </td>
          <td>
            <div>{organization?.owner?.fullName}</div>
            {organization?.owner?.email}
          </td>
          <td>
            <Styled.Tag>{capitalizeSingleWord(organization?.fraudRating)}</Styled.Tag>
          </td>
          <td>
            {job ? (
              <div>{job.title}</div>
            ) : organization.firstPublishedJobTitle ? (
              <div>{organization.firstPublishedJobTitle}</div>
            ) : (
              <span style={{ color: "#999" }}>No job</span>
            )}
          </td>
          <td>{job ? job.jobApplicationsCount : organization.firstPublishedJobApplicationsCount || 0}</td>
          <td>
            {organization.firstPublishedJobUrl ? (
              <a href={organization.firstPublishedJobUrl} target="_blank" rel="noopener noreferrer">
                View Job
              </a>
            ) : null}
          </td>
          <td>
            <Button
              styleType="text"
              type="button"
              onClick={() => handleBecomeUser(organization.owner.id)}
              disabled={disableBecome}
            >
              Become
            </Button>
          </td>
          <td>
            <DropdownMenu icon="settings" label="Actions">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  props.history.push(`/admin/org-jobs?organizationId=${organization.id}`);
                }}
              >
                View Jobs
              </button>
              <button onClick={() => handleOnClickEditOrganization(organization)}>
                Edit organization
              </button>
              {job && (
                <button onClick={() => handleOnClickEditJob(job)}>Edit job</button>
              )}
              <button onClick={() => handleOnClickUpdateSubscriptions(organization)}>
                Update careers page subscriptions
              </button>
              <button onClick={() => handleOnClickTakeover(organization)}>Shutdown</button>
            </DropdownMenu>
          </td>
        </tr>
      );
    });
  };

  const orgCount = organizationNodes().length;

  return (
    <Styled.Component>
      <Styled.Header>
        <h2>
          {orgCount} Free Plan Organizations with Published Jobs <span>{false && orgCount}</span>
        </h2>

        <Styled.HeaderControls>
          <FormInput
            name="search"
            placeholder="Search"
            value={searchTerm}
            onChange={onChangeSearchOrganizations}
          />
        </Styled.HeaderControls>
      </Styled.Header>

      <Styled.Table>
        <tbody>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Jobs</th>
            <th>Owner</th>
            <th>Fraud Rating</th>
            <th>Published Job</th>
            <th>Applications</th>
            <th>Job URL</th>
            <th />
            <th />
          </tr>
          {organizationNodes()}
        </tbody>
      </Styled.Table>

      <Styled.LoadMore>
        <Button
          styleType="secondary"
          type="button"
          onClick={handleLoadMore}
          loading={isFetchingNextPage}
          disabled={isFetchingNextPage || !hasNextPage}
        >
          Load More
        </Button>
      </Styled.LoadMore>


      {isFetchingFree && (
        <Styled.Loading>Loading...</Styled.Loading>
      )}
    </Styled.Component>
  );
}

AdminDashboardFreeJobs.propTypes = {};

AdminDashboardFreeJobs.defaultProps = {};

export default AdminDashboardFreeJobs;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Component = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardFreeJobs;
    ${t.p(4)};
  `;
});

Styled.Header = styled.header((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardFreeJobs_Header;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    h2 {
      ${[t.mt(1), t.text.h3, t.mb(2)]};
      display: flex;
      align-items: center;
    }
    span {
      ${[t.ml(2), t.text.sm, t.text.secondary, t.text.normal]};
    }
  `;
});

Styled.HeaderControls = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardFreeJobs_HeaderControls;
    display: flex;
    align-items: flex-end;
    gap: ${t.spacing[4]};
  `;
});

Styled.ControlItem = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardFreeJobs_ControlItem;
    ${t.mb(5)};
  `;
});

Styled.Table = styled.table((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardFreeJobs_Table;
    border-collapse: collapse;
    width: 100%;

    th {
      ${[t.pb(2), t.text.xs, t.text.bold]};
      text-align: start;
      ${t.text.primary};
    }

    tr {
      border-bottom: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
      ${t.text.secondary};
    }

    tr:last-of-type {
      border-bottom: none;
    }

    svg {
      stroke: ${t.color.black};
    }

    td,
    th {
      ${[t.p(2)]};

      &:first-of-type {
        ${t.pl(0)}
      }

      &:last-of-type {
        ${t.pr(0)}
      }
    }
  `;
});

Styled.Tag = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardFreeJobs_Tag;
    ${[t.h(6), t.px(2), t.text.xs, t.rounded.xs]}
    display: inline-flex;
    align-items: center;
    color: ${t.dark ? t.color.gray[200] : t.color.gray[600]};
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    line-height: 1.4;
    overflow: hidden;
    span {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  `;
});

Styled.LoadMore = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardFreeJobs_LoadMore;
    ${[t.mt(4), t.mb(4), t.text.center]};
    display: block;
    width: 100%;
  `;
});

Styled.Loading = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AdminDashboardFreeJobs_Loading;
    ${[t.py(4), t.text.center, t.text.secondary]};
  `;
});
